import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { teamNeedsTelemetryOnboarding } from '@/services/teamGameProfileService';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requireEnterpriseAdmin?: boolean;
  requireRole?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAdmin = false,
  requireEnterpriseAdmin = false,
  requireRole
}) => {
  const { user, profile, loading, hasRole, isAdmin, isEnterpriseAdmin } = useAuth();
  const location = useLocation();
  const [waitingForProfile, setWaitingForProfile] = useState(false);
  const [profileWaitTimeout, setProfileWaitTimeout] = useState(false);

  // 'checking'  — AAL level not yet determined (async call in flight)
  // 'ok'        — session meets the required assurance level, allow through
  // 'needs-mfa' — aal1 session but aal2 is required; redirect to /auth
  const [aalStatus, setAalStatus] = useState<'checking' | 'ok' | 'needs-mfa'>('checking');
  const [needsTelemetry, setNeedsTelemetry] = useState(false);

  useEffect(() => {
    if (!user) {
      setAalStatus('checking'); // reset for the next sign-in
      return;
    }
    supabase.auth.mfa.getAuthenticatorAssuranceLevel().then(({ data }) => {
      if (data?.nextLevel === 'aal2' && data.nextLevel !== data.currentLevel) {
        setAalStatus('needs-mfa');
      } else {
        setAalStatus('ok');
      }
    });
  }, [user]);

  // Track if we're waiting for a profile to be created
  useEffect(() => {
    if (user && !profile && !loading) {
      // Wait 1 second before showing "Setting up your profile..."
      // This prevents showing it during normal profile fetch on page reload
      const delayTimeout = setTimeout(() => {
        setWaitingForProfile(true);
      }, 1000);

      // Set a timeout to prevent infinite waiting (10 seconds total)
      const maxTimeout = setTimeout(() => {
        setProfileWaitTimeout(true);
      }, 10000);

      return () => {
        clearTimeout(delayTimeout);
        clearTimeout(maxTimeout);
      };
    } else {
      setWaitingForProfile(false);
      setProfileWaitTimeout(false);
    }
  }, [user, profile, loading]);

  useEffect(() => {
    const teamId = profile?.active_team_id;
    if (!user || !profile?.onboarding_completed || !teamId) {
      setNeedsTelemetry(false);
      return;
    }
    let cancelled = false;
    teamNeedsTelemetryOnboarding(user.id, teamId)
      .then((needed) => { if (!cancelled) setNeedsTelemetry(needed); })
      .catch(() => { if (!cancelled) setNeedsTelemetry(false); });
    return () => { cancelled = true; };
  }, [user, profile?.onboarding_completed, profile?.active_team_id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Wait for the async AAL check to complete before rendering anything
  if (aalStatus === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // Session exists but MFA step-up is required — send back to /auth where
  // Auth.tsx will detect the aal2 requirement and show the TOTP challenge
  if (aalStatus === 'needs-mfa') {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Show "Setting up your profile..." if user exists but profile doesn't (and we haven't timed out)
  if (waitingForProfile && !profileWaitTimeout && location.pathname !== '/onboarding') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Setting up your profile...</p>
        </div>
      </div>
    );
  }

  // If we timed out waiting for profile, show error
  if (profileWaitTimeout && !profile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <p className="text-destructive mb-4">Profile setup failed or is taking too long.</p>
          <p className="text-muted-foreground text-sm">Please contact support or try logging out and back in.</p>
        </div>
      </div>
    );
  }

  // Redirect to onboarding if user hasn't completed it (except if already on onboarding page)
  if (profile && !profile.onboarding_completed && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  if (
    needsTelemetry
    && location.pathname !== '/onboarding/telemetry'
    && location.pathname !== '/onboarding'
  ) {
    return <Navigate to="/onboarding/telemetry" replace />;
  }

  // Guard: if a role-based check is required but profile/roles haven't loaded yet,
  // keep showing the spinner rather than evaluating isAdmin() against an empty
  // roles array. This prevents a false redirect to "/" during the async fetch window
  // (AuthContext sets loading=false immediately but fetches the profile async).
  const requiresRoleCheck = requireAdmin || requireEnterpriseAdmin || !!requireRole;
  if (requiresRoleCheck && !profile && !profileWaitTimeout) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (requireAdmin && !isAdmin()) {
    return <Navigate to="/" replace />;
  }

  if (requireEnterpriseAdmin && !isEnterpriseAdmin()) {
    return <Navigate to="/" replace />;
  }

  if (requireRole && !hasRole(requireRole)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
